import { Await, Link, useLocation, useNavigate, useRouteLoaderData } from '@remix-run/react';
import { SlidersHorizontal } from 'lucide-react';
import { Suspense, useCallback, useEffect, useRef } from 'react'
import { getCountryName, getFormattedCountryName } from '~/lib/helpers/constants';
import { getCurrentCustomerCount, parseSearchUrl } from '~/lib/helpers/StringHelper';
import {
  clearStaleSearchPlaceContextForCityLevelPath,
  getPersistedSearchLocationContext,
} from '~/lib/helpers/searchPlaceContext';
import { buildLocationUrlWithOfficeTypeFilter } from '~/components/blocks/Geosearch/locationGeosearch';
import { LazyLoader } from '~/lib/utils/LazyLoader';
import { ClientOnly } from '../common/client-only';
import { ListOnOfficeHub } from '../common/ListOnOfficeHub';
import OfficeSearchBanner from '../common/OfficeBanner';
import AutoPlaces from '../Geosearch/AutoPlaces';
import ProviderSliderSection from '../Sections/ProviderSliderSection';
import { AboutSection } from './AboutSection';
import { GoogleReviews } from './GoogleReviews/GoogleReviews';
import { GuideAndTips } from './GuideAndTips';
import { PopularLocations } from './PopularLocations';
import WhyOfficeHub from './WhyOfficeHub';
import WorkspaceFinder from './WorkspaceFinder';
import OfficeOptions from './WorkspaceOptions';
import Container from '~/components/atoms/Container';
import MobileDrawer from '../Geosearch/MobileDrawer';
import NearByProperties from '../common/NearByProperties';
import { getRegionUrlPrefix } from '~/lib/helpers/slugHelper';
import {
  PropertiesSkeleton,
  PartnersSkeleton,
  ListingsSkeleton,
  BlogsSkeleton,
} from '~/components/ui/HomeSkeletons';
import { numberFormatter } from '~/lib/helpers/numberFormatHelper';
import { OhButton } from '~/components/ui/custom-radix-components/button';
import { HappyClientsAvatars } from '../common/HappyClientsAvatars';
import PropertyGptSearchPromo from './PropertyGptSearchPromo';
import { useLoading } from '~/lib/contexts/LoadingContext';
import { cn } from '~/lib/utils';
import { HOME_PAGE_SECTION_IDS } from "./constants";
import FAQs from "../FAQs/FAQs";
import { buildHomeChatPageContext } from "./buildHomeChatPageContext";

function MobileLayout({
  categories,
  drawer,
  setDrawer,
  url,
  query,
  callGeoSearch,
  callGeoSearchForDrawer,
  onApplyDrawerFilters,
  filterData,
  setUrl,
  searchUrl,
  loaderData,
  region,
  countryCode,
  countryName,
  maxPrice,
  state,
  city,
  initialUserCurrency,
  geoData,
  parsedQuery,
  regionData
}) {
  const navigate = useNavigate();
  const { showSpinner } = useLoading();
  const showChatbot = (useRouteLoaderData("root") as { showChatbot?: boolean } | undefined)?.showChatbot;
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const location = useLocation()

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (typeof window === "undefined") return;

      const payload = await buildHomeChatPageContext({
        region,
        countryName,
        pathname: location?.pathname,
        categories,
        initialUserCurrency,
        partners: loaderData?.partners,
        nearByProperties: loaderData?.nearByProperties,
        featuredPlaces: loaderData?.featuredPlaces,
        blogs: loaderData?.blogs,
        homeSummary: loaderData?.homeSummary,
      });

      if (cancelled) return;
      (window as any).__OH_CHAT_PAGE_CONTEXT__ = payload;
    })();

    return () => {
      cancelled = true;
    };
  }, [categories, loaderData, region, countryName, location?.pathname, initialUserCurrency]);

  const clearRedirectTimer = useCallback(() => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
  }, []);

  const combineUrlAndQuery = useCallback(
    (baseUrl: string, queryString: string): string => {
      return buildLocationUrlWithOfficeTypeFilter(
        baseUrl,
        queryString,
        filterData?.officeTypes,
      );
    },
    [filterData?.officeTypes],
  );

  const navigateToSearchUrl = useCallback((nextUrl: string) => {
    if (!nextUrl) return;
    showSpinner();
    const isAbsolute = /^https?:\/\//i.test(nextUrl);

    if (isAbsolute) {
      try {
        clearStaleSearchPlaceContextForCityLevelPath(new URL(nextUrl).pathname);
      } catch {
        // ignore invalid URLs
      }
    } else {
      const pathOnly = nextUrl.split("?")[0];
      clearStaleSearchPlaceContextForCityLevelPath(pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`);
    }

    // Ensure at least one paint frame before navigation so GlobalSpinner is visible.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (isAbsolute) {
          window.location.assign(nextUrl);
          return;
        }
        navigate(nextUrl);
      });
    });
  }, [navigate, showSpinner]);

  useEffect(() => {
    return () => {
      clearRedirectTimer();
    };
  }, [clearRedirectTimer]);

  useEffect(() => {
    const cancelPendingRedirect = () => clearRedirectTimer();
    const cancelEvents = ["pointerdown", "touchstart", "keydown", "wheel", "scroll"] as const;

    cancelEvents.forEach((eventName) => {
      window.addEventListener(eventName, cancelPendingRedirect, { passive: true });
    });

    return () => {
      cancelEvents.forEach((eventName) => {
        window.removeEventListener(eventName, cancelPendingRedirect);
      });
    };
  }, [clearRedirectTimer]);

  const handleLocationSelect = (loc: string) => {
    clearRedirectTimer();
    if (!loc) {
      setUrl(null);
      return;
    }

    let cleanLoc = loc.replace(/\/\?/g, "?").replace(/\?$/, "");
    if (!cleanLoc.startsWith("http://") && !cleanLoc.startsWith("https://") && !cleanLoc.startsWith("/")) {
      cleanLoc = `/${cleanLoc}`;
    }
    if (cleanLoc.includes("?")) {
      const [path, urlQuery] = cleanLoc.split("?");
      cleanLoc = path.replace(/\/$/, "") + (urlQuery ? `?${urlQuery}` : "");
    } else {
      cleanLoc = cleanLoc.replace(/\/$/, "");
    }

    let urlToStore = cleanLoc;
    if (!cleanLoc.startsWith("http://") && !cleanLoc.startsWith("https://")) {
      urlToStore = `${window.location.origin}${cleanLoc.startsWith("/") ? cleanLoc : `/${cleanLoc}`}`;
    }

    setUrl(urlToStore);
    const parsed = parseSearchUrl(loc) as Record<string, any>;
    // Clean URLs no longer carry the coordinates, so fall back to the
    // background location context persisted when the place was selected.
    const ctx = getPersistedSearchLocationContext();
    const lat = parsed?.latitude ?? ctx?.latitude;
    const lng = parsed?.longitude ?? ctx?.longitude;
    const countryCode = parsed?.country ?? ctx?.country;
    if (lat && lng && countryCode) {
      // store cookie for 30 days
      document.cookie = `lastSearchedLocation=${encodeURIComponent(
        JSON.stringify({
          lat,
          lng,
          country: countryCode,
        })
      )}; path=/; max-age=${60 * 60 * 24 * 30}`;

      redirectTimerRef.current = setTimeout(() => {
        const finalUrl = combineUrlAndQuery(urlToStore, query);
        navigateToSearchUrl(finalUrl);
      }, 3000);
    }
  };

  const targetSearchUrl = url
    ? combineUrlAndQuery(url, query)
    : searchUrl;



  return (
    <div className="flex flex-col w-full justify-center pt-48 montserrat overflow-hidden">
      <img
        src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/home-mobile.avif`} // replace with your office image
        alt="Office"
        className="w-full h-64 object-cover fixed"
        style={{ top: "var(--oh-region-banner-h, 0px)" }}
        fetchPriority='high'
      />

      {/* Hero Section */}

      <section id={HOME_PAGE_SECTION_IDS.hero} className="relative w-full scroll-mt-28">
        {/* Card Section */}
        <div className="bg-white rounded-t-3xl p-5 -mt-10  relative">
          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 leading-tight mb-6">
            Find & Rent Coworking, Serviced and Shared Offices in {getFormattedCountryName(region)}
          </h1>

          <div
            className={cn(
              "flex flex-col w-full",
              showChatbot && "rounded-xl border border-gray-200 shadow-sm",
            )}
          >
            <div className={cn("bg-white space-y-3 overflow-visible p-4 rounded-xl", !showChatbot && "rounded-xl")}>
              <div className="relative w-full">
                {/* Scrollable pills */}
                <div className="flex gap-2 overflow-x-auto whitespace-nowrap scrollbar-hide pr-10">
                  {categories.map((cat) => {
                    return (
                      <button
                        key={cat.value}
                        onClick={() => {
                          clearRedirectTimer();

                          if (filterData?.officeTypes?.includes(cat.value)) {
                            if (cat.value === "All") return;

                            const filteredTypes = filterData?.officeTypes?.filter(
                              (i: string) => i !== cat.value
                            );

                            callGeoSearch({ workspaceType: filteredTypes.join(",") });
                          } else {
                            if (cat.value !== "All") {
                              const filteredTypes = [
                                ...(filterData?.officeTypes || []),
                                cat.value,
                              ].filter((i) => i !== "All");

                              callGeoSearch({ workspaceType: filteredTypes.join(",") });
                            } else {
                              callGeoSearch({ workspaceType: "All" });
                            }
                          }
                        }}
                        className={`flex-shrink-0 px-4 py-1.5 text-sm rounded-full border transition ${filterData?.officeTypes?.includes(cat.value)
                          ? "border-oh-primary bg-teal-50 text-oh-primary"
                          : "border-gray-300 bg-white text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        {cat.option}
                      </button>
                    );
                  })}
                </div>

                {/* Filter Button Container with Gradient Overlay */}
                <div className="absolute right-0 top-0 bottom-0 flex items-center pl-6 bg-gradient-to-l from-white via-white to-transparent pointer-events-none">
                  <button
                    aria-label="Filters"
                    onClick={() => {
                      clearRedirectTimer();
                      setDrawer(true);
                    }}
                    className="pointer-events-auto p-2 rounded-full border border-gray-300 bg-white hover:bg-gray-100 shadow-sm z-10"
                  >
                    <SlidersHorizontal className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
              {drawer && (
                <MobileDrawer
                  url={url ? combineUrlAndQuery(url, query) : undefined}
                  open={drawer}
                  setOpen={setDrawer}
                  callGeoSearch={callGeoSearchForDrawer}
                  filterData={filterData}
                  data={null}
                  maxPrice={maxPrice}
                  onApply={onApplyDrawerFilters}
                  isHomePage
                />
              )}

              {/* Search Bar */}
              <div className="flex flex-col items-center space-y-2 gap-2">
                <div className="relative flex-1 w-full">
                  <ClientOnly>
                    {() => (
                      <AutoPlaces
                        onLocationSelect={handleLocationSelect}
                        inputStyleClassName=" shadow-none"
                      />
                    )}
                  </ClientOnly>
                </div>
                <OhButton
                  className="px-6 h-10 flex items-center justify-center rounded-full w-full"
                  onClick={() => {
                    if (targetSearchUrl) {
                      clearRedirectTimer();
                      navigateToSearchUrl(targetSearchUrl);
                    }
                  }}
                  disabled={!targetSearchUrl}
                >
                  Search
                </OhButton>
              </div>

            </div>

            <PropertyGptSearchPromo countryName={countryName} />
          </div>
          {/* Rating + Customers */}
          <Link
            to={`${getRegionUrlPrefix(countryName)}/testimonials`}
            target='blank'
            prefetch="intent"
          >
            <div className="flex flex-col xs:flex-row items-center justify-center gap-3 py-4 text-center mt-4">
              {/* Avatars */}
              <HappyClientsAvatars size={40} imageClassName="w-10 h-10" />
              <Await resolve={loaderData.homeSummary}>
                {(homeSummary) => (
                  <>
                    <div className="text-center">
                      <p className="text-gray-900 text-base font-extrabold ">
                        {getCurrentCustomerCount()}+
                      </p>
                      <p className="text-gray-900 text-xs font-normal ">Happy Clients</p>
                    </div>
                    <div className="flex items-center justify-center px-3 py-2 rounded-md text-base font-semibold">
                      <img
                        src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/google-fav-logo.avif`}
                        alt="Google Logo"
                        width={32}
                        height={32}
                        className=" mr-2 border-r-2 w-8 h-8 object-contain  pr-2"
                        loading="lazy"
                      />
                      <span>⭐ {parseFloat(homeSummary.sumRatings) > 0 ? parseFloat(homeSummary.sumRatings).toFixed(1) : '5.0'}</span>
                    </div>
                  </>
                )}
              </Await>
            </div>
          </Link>
        </div>
      </section>


      <div className='z-10 bg-white'>
        <WorkspaceFinder region={region} country={getCountryName(region)} />
        {/* Partners (deferred) */}
        <Container htmlFor="ultra-wide">
          <Suspense fallback={<PropertiesSkeleton />}>
            <Await resolve={loaderData.nearByProperties}>
              {(nearByProperties) => (
                nearByProperties ? <div id={HOME_PAGE_SECTION_IDS.topLocations} className="my-8 px-4 scroll-mt-28">
                  <NearByProperties carousel heading={`Top Office Locations in ${getFormattedCountryName(region)}`} subHeading={`Explore coworking, serviced and shared offices in ${getFormattedCountryName(region)}’s key business hubs.`} nearByProperties={nearByProperties} region={region} path={location.pathname} className={'flex overflow-x-auto scrollbar-hide'} />
                </div> : null
              )}
            </Await>
          </Suspense>
        </Container>

        <OfficeOptions region={region} country={getCountryName(region)} />

        <div className='py-4'>
          <WhyOfficeHub region={region} country={getCountryName(region)} searchUrl={`${searchUrl}`} isMobile={true} />
        </div>

        {/* Featured places will be loaded client-side to prevent layout shift */}


        {/* Partners (deferred) */}
        <Container htmlFor="ultra-wide">
          <div id={HOME_PAGE_SECTION_IDS.partners} className="py-8 px-4 scroll-mt-28">
            {/* Partners will be loaded client-side to prevent layout shift */}
            <Suspense fallback={<PartnersSkeleton />}>


              {/* Partners will be loaded client-side to prevent layout shift */}
              < Await resolve={loaderData.partners}>
                {(partners) => (
                  <ProviderSliderSection
                    providers={partners}
                    heading="Explore Offices from Our Premium Partners"
                    description={"Access exclusive workspaces from top global partners."}
                  />
                )}
              </Await>
            </Suspense>
          </div>
        </Container>
        <LazyLoader>
          <Suspense fallback={<ListingsSkeleton />}>
            <Await resolve={geoData()}>
              {(resolvedGeo) => {
                let currency = resolvedGeo?.[0]?.currencyIsoCode || initialUserCurrency;
                return (
                  <div id={HOME_PAGE_SECTION_IDS.listOnOfficeHub} className="scroll-mt-28">
                    <Container>
                      <ListOnOfficeHub
                        isMobile={true}
                        initialGeoData={resolvedGeo}
                        initialCity={city}
                        initialState={state}
                        initialLocation={countryCode}
                        initialUserCurrency={currency}
                        parsedQuery={parsedQuery}
                        regionData={regionData}
                        headingAs="h2"
                        staticTitle={`List Your Office Space in ${getFormattedCountryName(region)}`}
                      />
                    </Container>
                  </div>
                );
              }}
            </Await>
          </Suspense >
        </LazyLoader >

        <div className='mt-6'>
          <AboutSection mobile={true} region={region} country={countryName} />

        </div>

        <Container htmlFor="ultra-wide" className='px-4 py-8'>
          <GoogleReviews country={getCountryName(region)} isMobile={true} />
        </Container>

        <Suspense fallback={null}>
          <Await resolve={loaderData.faqs}>
            {(faqs) => faqs?.length ? (
              <div className="p-4 bg-white rounded-lg border-none max-w-7xl mx-auto">
                <FAQs title={`Answers to Common Office Space Queries in ${getFormattedCountryName(region)}`} faqs={faqs} />
              </div>
            ) : null}
          </Await>
        </Suspense>

        {/* Blogs will be loaded client-side to prevent layout shift */}
        <Suspense fallback={<BlogsSkeleton />}>
          <Await resolve={loaderData.blogs}>

            {(blogs) => blogs.blogs.length > 2 ? (
              <div className="py-8 bg-gray-50 px-4">
                <GuideAndTips blogs={blogs.blogs} region={getCountryName(region)} />
              </div>
            ) : null}
          </Await>
        </Suspense>

        <OfficeSearchBanner country={region} />
        {/* <Suspense fallback={<div>Loading Spaces...</div>}>
          <Await resolve={loaderData.cities}>
            {(states) =>
              states.results.length ?
                <Container htmlFor='ultra-wide'>

                  <BrowseOfficeSpaces data={states} region={region} />
                </Container> : null
            }

          </Await>
        </Suspense> */}
      </div >
    </div >
  )
}

export default MobileLayout